import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Printer, Sparkles, Tv, X } from "lucide-react";
import { api, API } from "../lib/api";
import { IMAGES } from "../lib/tokens";
import { useWorld } from "../state/World";
import LiveToggle from "../components/LiveToggle";
import { Receipt } from "../components/FeeLines";
import BlueprintDoc from "../components/BlueprintDoc";
import HorizonMap from "../components/HorizonMap";
import ScreenScene, { DESK_SCREEN, WALL_SCREEN } from "../components/ScreenScene";

/**
 * The Design Desk.
 *
 * The Table sorted the work. The Desk commits it: MelzAI dates the Sequence,
 * writes each step out as something a contractor could act on, and names the
 * homework each one depends on. The monitor carries the Horizon Map; the wall
 * screen carries the finished state.
 *
 * The Desk itself is free. The help costs, and only when it is actually used.
 */
export default function Desk() {
  const { refresh, isLive } = useWorld();
  const nav = useNavigate();
  const [blueprint, setBlueprint] = useState(null);
  const [stack, setStack] = useState(null);
  const [tx, setTx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [liveSpent, setLiveSpent] = useState(false);
  const [zoom, setZoom] = useState(false);
  const [picked, setPicked] = useState(null);

  const load = useCallback(async () => {
    const [bp, project] = await Promise.all([
      api.get("/stageward/blueprint"),
      api.get("/stageward/project"),
    ]);
    setBlueprint(bp.data.blueprint);
    const stacks = project.data.stacks || [];
    setStack(stacks[stacks.length - 1] || null);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dateIt = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/stageward/desk", { live: isLive() });
      setBlueprint(data.blueprint);
      setTx(data.transaction);
      setLiveSpent(Boolean(data.live_spent));
      await refresh();
      toast.success(
        data.live_spent ? "MelzAI wrote that one live." : "The Sequence has dates on it."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The Desk would not take it.");
    } finally {
      setBusy(false);
    }
  };

  const renderWall = async () => {
    setRendering(true);
    try {
      const { data } = await api.post("/stageward/render");
      setBlueprint((b) => ({ ...b, image_id: data.image_id }));
      if (!data.already) {
        setTx(data.transaction);
        await refresh();
        toast.success("The wall screen came on.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The screen stayed dark.");
    } finally {
      setRendering(false);
    }
  };

  const patchStep = async (n, body) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/stageward/blueprint/step/${n}`, body);
      setBlueprint(data.blueprint);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That did not save.");
    } finally {
      setBusy(false);
    }
  };

  const wallSrc = blueprint?.image_id ? `${API}/stageward/wall-image/${blueprint.image_id}` : null;

  return (
    <div className="relative min-h-screen bg-[var(--ink)] pb-32">
      <div className="mx-auto max-w-6xl px-5 pt-8 md:px-8">
        <div className="no-print mb-8 flex flex-wrap items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => nav("/stageward/table")}
            data-testid="desk-back-to-table"
            className="mono flex items-center gap-2 text-[10px] text-white/35 transition-colors hover:text-[var(--stage-gold)]"
          >
            <ArrowLeft size={12} /> back to the Table
          </button>
          <LiveToggle accent="gold" surface="desk" />
        </div>

        <header className="no-print mb-8">
          <p className="tracked mb-2 text-[9px] text-[var(--stage-gold)]/70">
            THE DESIGN DESK · THE GREEN ROOM · CLARITY HALL
          </p>
          <h1 className="serif text-4xl font-light leading-[1.05] text-[#f7ecd8] sm:text-5xl">
            The Table put it in order.
            <span className="italic text-[var(--stage-gold)]"> The Desk gives it dates.</span>
          </h1>
          <p className="mt-4 max-w-xl text-base leading-relaxed text-white/55">
            An order without dates is still a wish. Sit down and MelzAI will date
            every step, write it out properly, and tell you what homework each one
            needs before it can start.
          </p>
        </header>

        {/* ---------------------------------------------------- the monitor */}
        <div className="no-print relative">
          <ScreenScene
            plate={IMAGES.swDeskScreen}
            rect={DESK_SCREEN}
            testid="desk-monitor"
            tint="rgba(150,190,255,0.16)"
          >
            {blueprint?.steps?.length ? (
              <HorizonMap steps={blueprint.steps} onPick={setPicked} />
            ) : (
              <div className="flex h-full items-center justify-center bg-[#05070b]">
                <p className="mono text-center text-[8px] leading-relaxed text-white/25 sm:text-[10px]">
                  THE MONITOR IS BLANK
                  <br />
                  <span className="text-white/15">nothing has been dated yet</span>
                </p>
              </div>
            )}
          </ScreenScene>

          {!blueprint && (
            <div className="mt-7 flex flex-wrap items-center gap-5">
              <button
                type="button"
                className="btn btn-gold"
                disabled={busy || !stack}
                onClick={dateIt}
                data-testid="date-the-sequence-button"
              >
                {busy ? (
                  <Loader2 className="mx-auto animate-spin" size={14} />
                ) : (
                  "Let MelzAI date the Sequence"
                )}
              </button>
              <p className="mono max-w-xs text-[10px] leading-relaxed text-white/30">
                {stack
                  ? "Costs Tokens, because MelzAI actually does the work. Dating your own steps is free."
                  : "Nothing to date yet — set a Sequence at the Table first."}
              </p>
            </div>
          )}
        </div>

        {/* what the map says about a node you tapped */}
        <AnimatePresence>
          {picked && (
            <motion.div
              className="no-print glass-warm mt-6 rounded-xl p-6"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              data-testid="horizon-node-detail"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="mono mb-1 text-[9px] tracking-[0.22em] text-[var(--stage-gold)]/70">
                    STEP {picked.n} · {(picked.window || "").toUpperCase()}
                  </p>
                  <h3 className="serif text-2xl text-[#f7ecd8]">{picked.title}</h3>
                  {picked.body && (
                    <p className="mt-2 max-w-xl text-[13px] leading-relaxed text-white/55">
                      {picked.body}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setPicked(null)}
                  className="text-white/25 hover:text-white/70"
                  aria-label="close"
                  data-testid="node-detail-close"
                >
                  <X size={14} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {blueprint && (
          <>
            {/* -------------------------------------------- the wall screen */}
            <div className="no-print mt-16">
              <p className="tracked mb-2 text-[9px] text-[var(--stage-gold)]/70">
                THE WALL SCREEN
              </p>
              <h2 className="serif mb-5 text-3xl font-light leading-tight text-[#f7ecd8]">
                And this is what it looks like
                <span className="italic text-[var(--stage-gold)]"> when it is done.</span>
              </h2>

              <ScreenScene
                plate={IMAGES.swSofaScreen}
                rect={WALL_SCREEN}
                testid="wall-screen"
                tint="rgba(242,200,119,0.2)"
              >
                {wallSrc ? (
                  <button
                    type="button"
                    onClick={() => setZoom(true)}
                    className="block h-full w-full"
                    data-testid="wall-image-expand"
                    aria-label="see it properly"
                  >
                    <motion.img
                      src={wallSrc}
                      alt="the finished state"
                      className="h-full w-full object-cover"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 2.4, ease: "easeOut" }}
                    />
                  </button>
                ) : (
                  <div className="flex h-full items-center justify-center bg-black">
                    <p className="mono text-[8px] tracking-[0.2em] text-white/20 sm:text-[9px]">
                      SCREEN OFF
                    </p>
                  </div>
                )}
              </ScreenScene>

              <div className="mt-7 grid gap-6 md:grid-cols-[minmax(0,1fr)_320px]">
                <div>
                  {blueprint.finished_state && (
                    <p
                      className="serif max-w-2xl text-lg italic leading-relaxed text-[#e8d6ae]"
                      data-testid="finished-state-text"
                    >
                      “{blueprint.finished_state}”
                    </p>
                  )}
                  <p className="mono mt-3 text-[10px] leading-relaxed text-white/28">
                    Written in the past tense on purpose. You are remembering it,
                    not planning it.
                  </p>
                </div>
                {!wallSrc && (
                  <div>
                    <button
                      type="button"
                      className="btn btn-gold w-full"
                      disabled={rendering}
                      onClick={renderWall}
                      data-testid="render-wall-button"
                    >
                      {rendering ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="animate-spin" size={14} /> painting it
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <Tv size={13} /> Put it on the wall
                        </span>
                      )}
                    </button>
                    <p className="mono mt-3 text-[10px] leading-relaxed text-white/30">
                      The only image MelzAI paints, and the most expensive move in
                      the World. Rendered once, then it is yours — you never pay
                      twice for the same finished state.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* -------------------------------------------- the document */}
            <div className="mt-16">
              <div className="no-print mb-6 flex flex-wrap items-center gap-4">
                <button
                  type="button"
                  className="btn"
                  onClick={() => window.print()}
                  data-testid="print-blueprint-button"
                >
                  <span className="flex items-center gap-2">
                    <Printer size={13} /> Print it
                  </span>
                </button>
                <button
                  type="button"
                  className="mono text-[10px] text-white/35 transition-colors hover:text-[var(--stage-gold)]"
                  disabled={busy}
                  onClick={dateIt}
                  data-testid="redate-button"
                >
                  <span className="flex items-center gap-1.5">
                    <Sparkles size={11} /> have MelzAI write it again
                  </span>
                </button>
                <span className="mono text-[9px] text-white/22">
                  your ticks and notes survive a re-write
                </span>
              </div>

              <BlueprintDoc
                blueprint={blueprint}
                busy={busy}
                onToggle={(s) => patchStep(s.n, { done: !s.done })}
                onNotes={(s, notes) => patchStep(s.n, { notes })}
              />
            </div>

            <div className="no-print mt-14 flex flex-wrap items-center gap-6">
              <button
                type="button"
                className="btn"
                onClick={() => nav("/stageward/mission")}
                data-testid="desk-to-mission"
              >
                Set the Mission &amp; export
              </button>
              <button
                type="button"
                onClick={() => nav("/ledger")}
                data-testid="desk-ledger-link"
                className="mono text-[10px] text-white/30 transition-colors hover:text-[var(--stage-gold)]"
              >
                → see what this cost on the ledger
              </button>
            </div>
          </>
        )}

        {tx && (
          <div className="no-print mt-10 max-w-md" data-testid="desk-receipt">
            <Receipt tx={tx} />
            {liveSpent && (
              <p className="mono mt-2 text-[9px] text-[var(--stage-gold)]/60">
                That pass was a real model call, not a script.
              </p>
            )}
          </div>
        )}
      </div>

      {/* the finished state, properly */}
      <AnimatePresence>
        {zoom && wallSrc && (
          <motion.div
            className="no-print fixed inset-0 z-50 flex items-center justify-center bg-black/92 p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setZoom(false)}
            data-testid="wall-image-zoom"
          >
            <img
              src={wallSrc}
              alt="the finished state"
              className="max-h-full max-w-5xl rounded-lg object-contain shadow-2xl"
            />
            <button
              type="button"
              className="absolute right-6 top-6 text-white/45 hover:text-white"
              aria-label="close"
              data-testid="wall-zoom-close"
            >
              <X size={20} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
